import React, { useEffect, useRef, useState } from "react";
import { ChevronDown, Menu, X } from "lucide-react";
import { Link } from "react-router-dom";
import Logo from "./Logo";
import { useRegion } from "../contexts/RegionContext";

const NAV_ITEMS = [
    { to: "/about", label: "About" },
    { to: "/services", label: "Services" },
    { to: "/training", label: "Training" },
    { to: "/careers", label: "Careers" },
    { to: "/partner", label: "Partner" },
    { to: "/csr", label: "CSR" },
    { to: "/faq", label: "FAQ" },
    { to: "/contact", label: "Contact" },
];

const RegionSwitcher = () => {
    const { region, regions, setRegion, detectedCountry } = useRegion();
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    return (
        <div className="relative" ref={ref}>
            <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                className="flex items-center gap-2 px-3 py-2 rounded-full border border-white/10 hover:border-gold/50 transition-colors text-sm"
                data-testid="region-switcher-button"
                aria-haspopup="listbox"
                aria-expanded={open}
            >
                <span className="text-base leading-none" aria-hidden="true">{region.flag}</span>
                <span className="font-mono text-xs uppercase tracking-wider text-ivory/80">
                    {region.code === "GLOBAL" ? "Global" : region.code} · {region.currency}
                </span>
                <ChevronDown className={`w-3.5 h-3.5 text-ivory/60 transition-transform ${open ? "rotate-180" : ""}`} />
            </button>
            {open && (
                <div
                    className="region-menu absolute right-0 mt-2 w-64 p-2 z-50"
                    role="listbox"
                    data-testid="region-switcher-menu"
                >
                    {detectedCountry && (
                        <div className="px-3 py-2 text-[10px] uppercase tracking-widest text-ivory/40 font-mono">
                            Detected: {detectedCountry}
                        </div>
                    )}
                    {regions.map((r) => (
                        <button
                            key={r.code}
                            type="button"
                            onClick={() => { setRegion(r.code); setOpen(false); }}
                            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm hover:bg-white/5 transition-colors ${region.code === r.code ? "bg-gold/10 text-gold" : "text-ivory/85"}`}
                            data-testid={`region-option-${r.code}`}
                        >
                            <span className="text-lg leading-none">{r.flag}</span>
                            <span className="flex-1 text-left">{r.name}</span>
                            <span className="font-mono text-[11px] text-ivory/40">
                                {r.currency}{r.rebate ? " · rebated" : ""}
                            </span>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

const Nav = () => {
    const [mobileOpen, setMobileOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 16);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    return (
        <header
            data-testid="site-header"
            className={`fixed top-0 left-0 right-0 z-40 transition-all duration-300 ${scrolled ? "backdrop-blur-xl bg-obsidian/70 border-b border-gold/10" : "bg-transparent"}`}
        >
            <div className="max-w-7xl mx-auto px-5 lg:px-10 h-16 flex items-center justify-between">
                <Link to="/" data-testid="logo-link" className="shrink-0">
                    <Logo />
                </Link>

                <nav className="hidden lg:flex items-center gap-6" aria-label="Primary">
                    {NAV_ITEMS.map((n) => (
                        <Link
                            key={n.to}
                            to={n.to}
                            className="text-[13px] tracking-wide text-ivory/70 hover:text-gold transition-colors"
                            data-testid={`nav-${n.to.replace("/", "")}`}
                        >
                            {n.label}
                        </Link>
                    ))}
                </nav>

                <div className="flex items-center gap-3">
                    <RegionSwitcher />
                    <Link to="/contact" className="hidden sm:inline-flex btn-gold !py-2 !px-4 text-sm" data-testid="nav-cta-talk">
                        Schedule a call
                    </Link>
                    <button
                        type="button"
                        className="lg:hidden text-ivory/80 p-2"
                        onClick={() => setMobileOpen((o) => !o)}
                        aria-label="Toggle menu"
                        data-testid="mobile-menu-toggle"
                    >
                        {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                    </button>
                </div>
            </div>

            {mobileOpen && (
                <div className="lg:hidden bg-obsidian/95 backdrop-blur-xl border-t border-gold/10" data-testid="mobile-menu">
                    <nav className="max-w-7xl mx-auto px-5 py-4 flex flex-col gap-1" aria-label="Mobile">
                        {NAV_ITEMS.map((n) => (
                            <Link
                                key={n.to}
                                to={n.to}
                                onClick={() => setMobileOpen(false)}
                                className="px-3 py-3 rounded-lg text-ivory/85 hover:bg-white/5 hover:text-gold transition-colors"
                                data-testid={`mobile-nav-${n.to.replace("/", "")}`}
                            >
                                {n.label}
                            </Link>
                        ))}
                        <Link to="/contact" onClick={() => setMobileOpen(false)} className="btn-gold mt-3" data-testid="mobile-nav-cta">
                            Schedule a call
                        </Link>
                    </nav>
                </div>
            )}
        </header>
    );
};

export default Nav;
